import styled from "styled-components";
import { Dispatch, FC } from "react";
import { useSelector } from "react-redux";

import {
  LargeTh,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import {
  MediumSortableTh,
  SmallSortableTh,
  StyledSectionHeading,
} from "./TopTopicsTable";
import { CombinedTopicsType, SortState } from "@/types/resources";
import { RootState } from "@/utils/store";
import ScrollableTable from "@/components/ScrollableTable";
import InfoTooltip from "@/components/InfoTooltip";
import Text from "@/components/Text";
import TopCombinedTopicsTableRow from "./TopCombinedTopicsTableRow";

export const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      min-width: 216px;
      max-width: 216px;
      padding-left: 24px;
      position: sticky;
      background-color: ${({ theme }) => theme.colors.white};
      z-index: 1;
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      position: relative;
      padding-right: 24px;
    }
  }
`;

const ExtraLargeTh = styled(Th)`
  min-width: 238px;
`;

const StyledTableHeader = styled(TableHeader)`
  margin-top: 30px;
`;

interface Props {
  combinedTopics: CombinedTopicsType[];
  optionsKey: string;
  activeTab: number;
  sort: SortState;
  setSort: Dispatch<React.SetStateAction<SortState>>;
  filteredChannelsIds: number[];
}

const TopCombinedTopicsTable: FC<Props> = ({
  combinedTopics,
  optionsKey,
  activeTab,
  sort,
  setSort,
  filteredChannelsIds,
}) => {
  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews[optionsKey]
  );

  return (
    <>
      <StyledTableHeader>
        <StyledSectionHeading>Combined topics</StyledSectionHeading>
      </StyledTableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Topics</Th>
              {tableViewOptions["MA Rating"] && (
                <SmallSortableTh
                  title="MA Rating"
                  setSort={() => null}
                  sort={sort}
                  property="maRating"
                >
                  <InfoTooltip
                    content="Technical analysis method from financial trading to forecast future trends"
                    title="MA Rating"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions["Total views"] && (
                <SmallSortableTh
                  title="Total views"
                  property="totalViews"
                  sort={sort}
                  setSort={setSort}
                />
              )}
              {tableViewOptions.Videos && (
                <SmallSortableTh
                  title="Videos"
                  property="videosCount"
                  sort={sort}
                  setSort={setSort}
                />
              )}
              {tableViewOptions.VPH7 && (
                <SmallSortableTh
                  title="VPH7"
                  property="vph7"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average number of views per hour in the last 7 days period among videos with selected topic"
                    title="VPH7"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions.VPH30 && (
                <SmallSortableTh
                  title="VPH30"
                  property="vph30"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average number of views per hour in the last 30 days period among videos with selected topic"
                    title="VPH30"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions["Combined VPH7"] && (
                <SmallSortableTh
                  title="Combined VPH7"
                  property="vph7Sum"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Sum of views per hour in the last 7 days period among videos with selected topic"
                    title="Combined VPH7"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions["Combined VPH30"] && (
                <SmallSortableTh
                  title="Combined VPH30"
                  property="vph30Sum"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Sum of views per hour in the last 30 days period among videos with selected topic"
                    title="Combined VPH30"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions["Average V7"] && (
                <MediumSortableTh
                  title="Average V7"
                  property="v7"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average number of views per first 7 days among videos with selected topic"
                    title="Average V7"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions["Average V30"] && (
                <MediumSortableTh
                  title="Average V30"
                  property="v30"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average number of views per first 30 days among videos with selected topic"
                    title="Average V30"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions.V7 && (
                <MediumSortableTh
                  title="V7"
                  property="v7Sum"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Number of views per first 7 days"
                    title="V7"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions.V30 && (
                <MediumSortableTh
                  title="V30"
                  property="v30Sum"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Number of views per first 30 days"
                    title="V30"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions["V7 Index"] && (
                <MediumSortableTh
                  title="V7 Index"
                  property="v7Index"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average V7 among videos with selected topic compared to channel's average V7 of the last 90 days"
                    title="V7 Index"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions["V30 Index"] && (
                <MediumSortableTh
                  title="V30 Index"
                  property="v30Index"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average V30 among videos with selected topic compared to channel's average V30 of the last 90 days"
                    title="V30 Index"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions["Virality Rate"] && (
                <MediumSortableTh
                  title="Virality Rate"
                  property="viralityRate"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Probability for topic to go viral. Ratio between total number of videos with selected topic and videos where v7_index > 2"
                    title="Virality Rate"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions.RPM && (
                <SortableTh
                  title="RPM"
                  property="rpm"
                  sort={sort}
                  setSort={setSort}
                >
                  <InfoTooltip
                    content="Average Revenue per 1000 views among videos with selected topic"
                    title="RPM"
                  />
                </SortableTh>
              )}
              {tableViewOptions["Trending in Views"] && (
                <LargeTh>
                  Trending in Views
                  <InfoTooltip
                    content="Total number of views of videos with selected topic; graph shows 14 weeks and compares first 7 weeks with last 7 weeks"
                    title="Trending in Views (Weekly)"
                  />
                  <br />
                  <Text as="span" $color="gray" $type="extraSmall">
                    Weekly
                  </Text>
                </LargeTh>
              )}
              {tableViewOptions.Note && <ExtraLargeTh>Note</ExtraLargeTh>}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={false}
              isError={false}
              hasData={!!combinedTopics}
              colSpan={6}
            >
              {combinedTopics.map((topic, index) => (
                <TopCombinedTopicsTableRow
                  key={index}
                  topic={topic}
                  optionsKey={optionsKey}
                  activeTab={activeTab}
                  filteredChannelsIds={filteredChannelsIds}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
    </>
  );
};

export default TopCombinedTopicsTable;
